<script setup lang="ts">
  import { computed } from "vue";
  import { Image, Play } from "lucide-vue-next";
  import { POPULATION_AUTO_COLORS, POPULATION_COLOR_PALETTES } from "@/lib/chartPalettes";
  import ResultsGeoMapChart, { type GeoZone } from "./Charts/ResultsGeoMapChart.vue";

  type ResultsPreviewKind =
    | "section"
    | "text"
    | "image"
    | "recode"
    | "crosstab"
    | "importance_performance"
    | "key_drivers"
    | "turf"
    | "kpi"
    | "kpi_trend"
    | "funnel"
    | "sankey"
    | "overlap"
    | "geo_map"
    | "word_cloud"
    | "bars"
    | "verbatims"
    | "media"
    | "video"
    | "audio";

  const props = defineProps<{
    kind: ResultsPreviewKind;
    title?: string;
    subtitle?: string;
  }>();

  const EXAMPLE_TITLES: Partial<Record<ResultsPreviewKind, string>> = {
    section: "Brand perception",
    crosstab: "Purchase intent by gender",
    importance_performance: "What drives satisfaction",
    key_drivers: "Where to act on satisfaction",
    turf: "The three flavours to launch",
    kpi: "Recommendation rate",
    kpi_trend: "Brand awareness evolution",
    funnel: "From awareness to purchase",
    sankey: "Preferred brand, before and after the campaign",
    overlap: "Customers who shop online and in store",
    geo_map: "Satisfaction level by department",
    word_cloud: "What matters most when buying",
  };
  const heading = computed(() => props.title || EXAMPLE_TITLES[props.kind] || "");

  const bars = [78, 54, 41, 22];
  const trend = [30, 42, 38, 55, 61, 72];
  const chartColor = POPULATION_AUTO_COLORS[0];
  const funnel = [
    { label: "Aware", share: 100, color: POPULATION_COLOR_PALETTES.blue[800] },
    { label: "Considered", share: 62, color: POPULATION_COLOR_PALETTES.blue[600] },
    { label: "Purchased", share: 26, color: POPULATION_COLOR_PALETTES.blue[400] },
  ];
  // Three answers on each side; each ribbon is a "same answer" or a "switch" flow.
  const flowNodes = [
    { x: 4, y: 2, h: 26, color: POPULATION_COLOR_PALETTES.blue[500] },
    { x: 4, y: 32, h: 18, color: POPULATION_COLOR_PALETTES.teal[500] },
    { x: 4, y: 54, h: 4, color: POPULATION_COLOR_PALETTES.purple[500] },
    { x: 92, y: 2, h: 22, color: POPULATION_COLOR_PALETTES.blue[500] },
    { x: 92, y: 28, h: 22, color: POPULATION_COLOR_PALETTES.teal[500] },
    { x: 92, y: 54, h: 4, color: POPULATION_COLOR_PALETTES.purple[500] },
  ];
  const ribbon = (y0: number, h0: number, y1: number, h1: number) =>
    `M8,${y0} C50,${y0} 50,${y1} 92,${y1} L92,${y1 + h1} C50,${y1 + h1} 50,${y0 + h0} 8,${y0 + h0} Z`;
  const flows = [
    { d: ribbon(2, 20, 2, 20), color: POPULATION_COLOR_PALETTES.blue[500] },
    { d: ribbon(22, 6, 28, 6), color: POPULATION_COLOR_PALETTES.blue[500] },
    { d: ribbon(32, 16, 34, 16), color: POPULATION_COLOR_PALETTES.teal[500] },
    { d: ribbon(48, 2, 54, 2), color: POPULATION_COLOR_PALETTES.teal[500] },
    { d: ribbon(54, 2, 22, 2), color: POPULATION_COLOR_PALETTES.purple[500] },
    { d: ribbon(56, 2, 56, 2), color: POPULATION_COLOR_PALETTES.purple[500] },
  ];
  const ipaItems = [
    { label: "Price", x: 22, y: 30 },
    { label: "Quality", x: 64, y: 24 },
    { label: "Design", x: 30, y: 62 },
    { label: "Packaging", x: 68, y: 72 },
    { label: "Delivery", x: 52, y: 45 },
  ];
  const drivers = [
    { label: "Service", weight: 31, score: 6.4 },
    { label: "Network", weight: 27, score: 7.9 },
    { label: "Price", weight: 22, score: 5.8 },
    { label: "App", weight: 12, score: 8.1 },
    { label: "Offers", weight: 8, score: 7.2 },
  ];
  const turfSteps = [
    { label: "Vanilla", share: 41, added: 41, color: POPULATION_COLOR_PALETTES.blue[700] },
    { label: "Pistachio", share: 63, added: 22, color: POPULATION_COLOR_PALETTES.blue[500] },
    { label: "Mango", share: 71, added: 8, color: POPULATION_COLOR_PALETTES.blue[300] },
  ];
  const overlapColors = [POPULATION_COLOR_PALETTES.blue[500], POPULATION_COLOR_PALETTES.teal[500]];
  const cityRespondents: Record<string, number> = {
    "75": 120,
    "92": 80,
    "93": 60,
    "94": 55,
    "78": 40,
    "91": 35,
    "95": 32,
    "77": 30,
    "69": 75,
    "13": 70,
    "59": 65,
    "33": 50,
    "31": 50,
    "44": 42,
    "06": 40,
    "67": 38,
    "34": 38,
    "35": 32,
    "38": 30,
  };
  const departementCodes = [
    ...Array.from({ length: 95 }, (_, index) => String(index + 1).padStart(2, "0")).filter((code) => code !== "20"),
    "2A",
    "2B",
  ];
  const mapZones: GeoZone[] = departementCodes.map((code, index) => {
    const respondents = cityRespondents[code] ?? 6 + ((index * 7) % 11);
    const satisfaction = 6 + ((index * 13) % 23) / 10;
    return { code, respondents, metricBase: respondents, value: satisfaction };
  });
  const cloud = [
    { text: "price", size: 26, color: POPULATION_COLOR_PALETTES.blue[700] },
    { text: "quality", size: 22, color: POPULATION_COLOR_PALETTES.teal[600] },
    { text: "delivery", size: 17, color: POPULATION_COLOR_PALETTES.purple[600] },
    { text: "fast", size: 14, color: POPULATION_COLOR_PALETTES.blue[400] },
    { text: "service", size: 13, color: POPULATION_COLOR_PALETTES.amber[600] },
    { text: "design", size: 11, color: POPULATION_COLOR_PALETTES.slate[600] },
    { text: "simple", size: 11, color: POPULATION_COLOR_PALETTES.teal[600] },
  ];
  const trendPoints = trend.map((value, index) => `${(index / (trend.length - 1)) * 100},${100 - value}`).join(" ");
</script>

<template>
  <div class="flex w-full flex-col gap-3">
    <template v-if="kind === 'section'">
      <div class="h-px w-full bg-border" />
      <p class="heading-title tracking-tight">{{ heading }}</p>
      <div class="flex flex-col gap-2">
        <div class="h-2 w-3/4 rounded-full bg-secondary" />
        <div class="h-2 w-1/2 rounded-full bg-secondary" />
      </div>
    </template>

    <template v-else-if="kind === 'text'">
      <div class="flex flex-col gap-2">
        <div class="h-2 w-full rounded-full bg-secondary" />
        <div class="h-2 w-11/12 rounded-full bg-secondary" />
        <div class="h-2 w-2/3 rounded-full bg-secondary" />
      </div>
      <div class="flex items-start gap-2 rounded-lg bg-status-info-soft px-3 py-2 text-xs text-status-info-strong">
        A callout, a quote or plain rich text between two charts.
      </div>
    </template>

    <template v-else-if="kind === 'image'">
      <div class="grid aspect-video place-items-center rounded-xl border bg-secondary">
        <Image class="size-6 text-foreground-subtle" />
      </div>
      <div class="h-2 w-1/3 rounded-full bg-secondary" />
    </template>

    <template v-else-if="kind === 'recode'">
      <div
        v-if="subtitle"
        class="flex flex-col gap-1">
        <span class="heading-label">Example</span>
        <p class="text-13 leading-normal text-foreground-secondary [overflow-wrap:anywhere]">{{ subtitle }}</p>
      </div>
    </template>

    <template v-else-if="kind === 'crosstab'">
      <p class="text-13 font-semibold text-primary">{{ heading }}</p>
      <div class="grid grid-cols-4 gap-px overflow-hidden rounded-lg border bg-border text-2xs">
        <div class="bg-muted px-2 py-1.5" />
        <div
          v-for="column in ['All', 'Women', 'Men']"
          :key="column"
          class="bg-muted px-2 py-1.5 font-semibold text-primary">
          {{ column }}
        </div>
        <template
          v-for="row in ['Yes', 'No', 'n=']"
          :key="row">
          <div class="bg-white px-2 py-1.5 font-medium text-foreground-secondary">{{ row }}</div>
          <div
            v-for="column in 3"
            :key="column"
            class="bg-white px-2 py-1.5 tabular-nums text-muted-foreground">
            {{ row === "n=" ? 50 * column : `${20 + column * 11}%` }}
          </div>
        </template>
      </div>
    </template>

    <template v-else-if="kind === 'importance_performance'">
      <p class="text-13 font-semibold text-primary">{{ heading }}</p>
      <div class="relative aspect-[4/3] rounded-lg border bg-white">
        <div class="absolute inset-x-0 top-1/2 h-px bg-border" />
        <div class="absolute inset-y-0 left-1/2 w-px bg-border" />
        <div
          v-for="item in ipaItems"
          :key="item.label"
          class="absolute flex -translate-y-1/2 items-center gap-1 text-2xs text-foreground-secondary"
          :style="{ left: `${item.x}%`, top: `${item.y}%` }">
          <span
            class="size-2.5 shrink-0 -translate-x-1/2 rounded-full"
            :style="{ backgroundColor: chartColor }" />
          {{ item.label }}
        </div>
        <span class="absolute bottom-1 right-2 text-2xs text-foreground-subtle">Performance →</span>
        <span class="absolute top-1 left-2 text-2xs text-foreground-subtle">↑ Importance</span>
      </div>
    </template>

    <template v-else-if="kind === 'key_drivers'">
      <p class="text-13 font-semibold text-primary">{{ heading }}</p>
      <div class="flex flex-col gap-1.5">
        <div
          v-for="driver in drivers"
          :key="driver.label"
          class="flex items-center gap-2 text-2xs tabular-nums text-muted-foreground">
          <span class="w-12 shrink-0 truncate text-right">{{ driver.label }}</span>
          <span class="h-3 rounded-sm" :style="{ width: `${driver.weight * 1.8}%`, backgroundColor: chartColor }" />
          <span class="font-semibold text-foreground">{{ driver.weight }}%</span>
          <span class="ml-auto shrink-0 text-foreground-subtle">{{ driver.score }}</span>
        </div>
      </div>
    </template>

    <template v-else-if="kind === 'turf'">
      <p class="text-13 font-semibold text-primary">{{ heading }}</p>
      <div class="flex flex-col gap-1.5">
        <div
          v-for="(step, index) in turfSteps"
          :key="step.label"
          class="flex items-center gap-2 text-2xs tabular-nums text-muted-foreground">
          <span class="w-14 shrink-0 truncate text-right">{{ index + 1 }}. {{ step.label }}</span>
          <span class="h-3 rounded-sm" :style="{ width: `${step.share * 0.6}%`, backgroundColor: step.color }" />
          <span class="font-semibold text-foreground">{{ step.share }}%</span>
          <span class="text-status-success-strong">+{{ step.added }}</span>
        </div>
      </div>
      <p class="text-2xs text-muted-foreground">Reach of the best combination, one answer at a time</p>
    </template>

    <template v-else-if="kind === 'kpi'">
      <p class="text-13 font-semibold text-primary">{{ heading }}</p>
      <p class="text-xs text-muted-foreground">Share of respondents who would recommend us</p>
      <p
        class="text-2xl font-semibold leading-none"
        :style="{ color: chartColor }">
        72%
      </p>
    </template>

    <template v-else-if="kind === 'kpi_trend'">
      <p class="text-13 font-semibold text-primary">{{ heading }}</p>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        class="h-24 w-full">
        <polyline
          :points="trendPoints"
          fill="none"
          :stroke="chartColor"
          stroke-width="2.5"
          vector-effect="non-scaling-stroke" />
      </svg>
      <div class="flex justify-between text-2xs text-foreground-subtle">
        <span>Wave 1</span>
        <span>Wave 6</span>
      </div>
    </template>

    <template v-else-if="kind === 'funnel'">
      <p class="text-13 font-semibold text-primary">{{ heading }}</p>
      <div class="flex flex-col items-center gap-1">
        <template
          v-for="(stage, index) in funnel"
          :key="index">
          <span
            v-if="index > 0"
            class="text-2xs text-muted-foreground">
            ↓ {{ Math.round((stage.share / funnel[index - 1].share) * 100) }}%
          </span>
          <div
            class="flex h-6 min-w-fit items-center justify-center whitespace-nowrap rounded-md px-2 text-2xs font-medium text-white"
            :style="{ width: `${stage.share}%`, backgroundColor: stage.color }">
            {{ stage.label }} · {{ stage.share }}%
          </div>
        </template>
      </div>
    </template>

    <template v-else-if="kind === 'sankey'">
      <p class="text-13 font-semibold text-primary">{{ heading }}</p>
      <svg
        viewBox="0 0 100 60"
        preserveAspectRatio="none"
        class="h-28 w-full">
        <path
          v-for="(flow, index) in flows"
          :key="index"
          :d="flow.d"
          :fill="flow.color"
          fill-opacity="0.35" />
        <rect
          v-for="(node, index) in flowNodes"
          :key="`n-${index}`"
          :x="node.x"
          :y="node.y"
          width="4"
          :height="node.h"
          :fill="node.color" />
      </svg>
      <div class="flex justify-between text-2xs text-foreground-subtle">
        <span>Before</span>
        <span>After</span>
      </div>
    </template>

    <template v-else-if="kind === 'overlap'">
      <p class="text-13 font-semibold text-primary">{{ heading }}</p>
      <svg
        viewBox="0 0 100 60"
        class="h-28 w-full">
        <circle
          cx="38"
          cy="30"
          r="24"
          :fill="chartColor"
          fill-opacity="0.45" />
        <circle
          cx="62"
          cy="30"
          r="20"
          :fill="overlapColors[1]"
          fill-opacity="0.45" />
        <text
          x="26"
          y="32"
          font-size="6"
          class="fill-foreground font-medium">
          A 54%
        </text>
        <text
          x="64"
          y="32"
          font-size="6"
          class="fill-foreground font-medium">
          B 43%
        </text>
      </svg>
      <div class="flex flex-wrap gap-1.5 text-2xs text-muted-foreground">
        <span class="rounded-full border px-2 py-0.5">A only 24%</span>
        <span class="rounded-full border px-2 py-0.5">A ∩ B 22%</span>
        <span class="rounded-full border px-2 py-0.5">B only 16%</span>
      </div>
    </template>

    <template v-else-if="kind === 'geo_map'">
      <p class="text-13 font-semibold text-primary">{{ heading }}</p>
      <ResultsGeoMapChart
        :zones="mapZones"
        has-metric
        metric-kind="average"
        :color="chartColor"
        :zoomable="false" />
    </template>

    <template v-else-if="kind === 'word_cloud'">
      <p class="text-13 font-semibold text-primary">{{ heading }}</p>
      <div class="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1 py-4 leading-none">
        <span
          v-for="word in cloud"
          :key="word.text"
          class="font-semibold"
          :style="{ fontSize: `${word.size}px`, color: word.color }">
          {{ word.text }}
        </span>
      </div>
    </template>

    <template v-else-if="kind === 'verbatims'">
      <p class="text-13 font-semibold text-primary">{{ heading }}</p>
      <div class="flex flex-col gap-2">
        <div
          v-for="n in 3"
          :key="n"
          class="rounded-lg border bg-white px-3 py-2 text-xs italic text-foreground-secondary">
          “{{ ["Very easy to use, I found what I needed.", "Too many steps before the checkout.", "Good value for the price."][n - 1] }}”
        </div>
      </div>
    </template>

    <template v-else-if="kind === 'media' || kind === 'video'">
      <p class="text-13 font-semibold text-primary">{{ heading }}</p>
      <div class="grid grid-cols-3 gap-2">
        <div
          v-for="n in 3"
          :key="n"
          class="grid aspect-[4/3] place-items-center rounded-lg border bg-secondary">
          <component
            :is="kind === 'video' ? Play : Image"
            class="size-5 text-foreground-subtle" />
        </div>
      </div>
    </template>

    <template v-else-if="kind === 'audio'">
      <p class="text-13 font-semibold text-primary">{{ heading }}</p>
      <div class="flex flex-col gap-2">
        <div
          v-for="n in 3"
          :key="n"
          class="flex items-center gap-2.5 rounded-full border bg-white px-3 py-2">
          <Play
            class="size-3.5 text-primary"
            :stroke-width="2" />
          <span class="h-1 flex-1 rounded-full bg-secondary" />
          <span class="text-2xs tabular-nums text-foreground-subtle">0:{{ 10 + n * 7 }}</span>
        </div>
      </div>
    </template>

    <template v-else>
      <p class="text-13 font-semibold text-primary">{{ heading }}</p>
      <p
        v-if="subtitle"
        class="text-2xs text-muted-foreground">
        {{ subtitle }}
      </p>
      <div class="flex flex-col gap-2">
        <div
          v-for="(width, index) in bars"
          :key="index"
          class="flex items-center gap-2 text-2xs tabular-nums text-muted-foreground">
          <span class="w-12 shrink-0 text-right">{{ ["Yes", "No", "Maybe", "N/A"][index] }}</span>
          <span class="h-3 rounded-sm" :style="{ width: `${width * 0.7}%`, backgroundColor: chartColor }" />
          <span>{{ width }}%</span>
        </div>
      </div>
    </template>
  </div>
</template>
